import { useCallback } from 'react';
import {
  FilterBar,
  FilterBarSize,
  FilterController,
  FilterList,
  type FilterSelection,
  type FilterSelections,
} from '@amzn/stencil-react-components/filtering';
import type { RiskScoreWindow } from '@amzn/gbsa-helios-typescript-client';
import { RISK_ENGINE_DEFAULT_WINDOW } from '../logic/riskScoreRequest';
import { buildTimeWindowOptions, decodeRiskScoreWindow } from '../logic/riskTimeWindow';

import { useMessages } from 'src/v2/shared/i18n/useMessages';
import { toFilterStringArray } from 'src/v2/shared/ui/filterSelection';
import { useLocationFilterElement } from 'src/v2/shared/ui/useLocationFilterElement';
import { RISK_ENGINE_BUNDLE, type RiskEngineMessageKey } from '../translations';
import type { RiskFilterOption } from '../logic/riskFilters';
import { RISK_ENGINE_TEST_IDS } from '../testIds';

export type { RiskFilterOption };

export interface RiskEngineFilters {
  readonly timeWindow: RiskScoreWindow;
  readonly locations: string[];
  readonly categories: string[];
  readonly metrics: string[];
  readonly statuses: string[];
}

interface RiskEngineFilterBarProps {
  readonly filters: RiskEngineFilters;
  readonly locationOptions: readonly string[];
  readonly categoryOptions: readonly RiskFilterOption[];
  readonly metricOptions: readonly RiskFilterOption[];
  readonly statusOptions: readonly RiskFilterOption[];
  readonly onApply: (filters: RiskEngineFilters) => void;
}

/**
 * Render all five filters (time window, location, category, metric, status) inline on the desktop
 * bar rather than collapsing the overflow into the "All filters" flyout — there is room for them,
 * and keeping them visible avoids a trip through the side panel. Stencil wraps the row on narrow
 * widths. Bump this if more facets are added.
 */
const RISK_ENGINE_VISIBLE_FILTER_COUNT = 5;

export function createDefaultRiskEngineFilters(): RiskEngineFilters {
  return {
    timeWindow: RISK_ENGINE_DEFAULT_WINDOW,
    locations: [],
    categories: [],
    metrics: [],
    statuses: [],
  };
}

function selectedTimeWindow(selection: FilterSelection): RiskScoreWindow {
  return decodeRiskScoreWindow(toFilterStringArray(selection)[0]);
}

export function riskEngineFiltersFromSelections(
  selections: FilterSelections,
): RiskEngineFilters {
  return {
    timeWindow: selectedTimeWindow(selections.timeWindow),
    locations: toFilterStringArray(selections.locations),
    categories: toFilterStringArray(selections.categories),
    metrics: toFilterStringArray(selections.metrics),
    statuses: toFilterStringArray(selections.statuses),
  };
}

/** Stencil filter-panel trigger for Risk Engine window, location, category, metric, and status. */
export function RiskEngineFilterBar({
  filters,
  locationOptions,
  categoryOptions,
  metricOptions,
  statusOptions,
  onApply,
}: RiskEngineFilterBarProps): JSX.Element {
  const { t } = useMessages<RiskEngineMessageKey>(RISK_ENGINE_BUNDLE);

  // Not memoized on `t`: useMessages keeps a stable `t` identity across the async bundle load, so a
  // `[t]` memo would freeze the options with the raw message keys that `t` returns before the bundle
  // resolves. Building inline each render (like RiskTimeWindowToggle) reflects the loaded labels.
  const timeWindowOptions = buildTimeWindowOptions(t);

  const handleApply = useCallback(
    (selections: FilterSelections): void => {
      onApply(riskEngineFiltersFromSelections(selections));
    },
    [onApply],
  );
  const handleTimeWindowChange = useCallback(
    (selection: FilterSelection): void => {
      onApply({ ...filters, timeWindow: selectedTimeWindow(selection) });
    },
    [filters, onApply],
  );
  const handleLocationChange = useCallback(
    (selection: FilterSelection): void => {
      onApply({ ...filters, locations: toFilterStringArray(selection) });
    },
    [filters, onApply],
  );
  const handleCategoryChange = useCallback(
    (selection: FilterSelection): void => {
      onApply({ ...filters, categories: toFilterStringArray(selection) });
    },
    [filters, onApply],
  );
  const handleMetricChange = useCallback(
    (selection: FilterSelection): void => {
      onApply({ ...filters, metrics: toFilterStringArray(selection) });
    },
    [filters, onApply],
  );
  const handleStatusChange = useCallback(
    (selection: FilterSelection): void => {
      onApply({ ...filters, statuses: toFilterStringArray(selection) });
    },
    [filters, onApply],
  );

  const locationFilterElement = useLocationFilterElement({
    options: locationOptions,
    value: filters.locations,
    onChange: handleLocationChange,
    titleText: t('filterLocationTitle'),
    resetButtonText: t('filterLocationClear'),
    listTitleText: t('filterLocationListTitle'),
  });

  return (
    <div data-testid={RISK_ENGINE_TEST_IDS.filters}>
      {/* Each facet carries unique options, value, and handler per field; the location control
          comes from a shared hook, so a .map() would not eliminate the structural repetition.
          standards-ignore: DRY:FilterController standards-ignore: DRY:FilterList */}
      <FilterBar
        flyoutApplyButtonText={t('filterApply')}
        flyoutResetButtonText={t('filterClearAll')}
        flyoutTitleText={t('filterFlyoutTitle')}
        flyoutTriggerButtonText={t('filtersButton')}
        onApply={handleApply}
        shouldShowAppliedFiltersRow
        size={FilterBarSize.Small}
        visibleFiltersMaxCount={RISK_ENGINE_VISIBLE_FILTER_COUNT}
      >
        <FilterController name="timeWindow" titleText={t('timeWindow')}>
          <FilterList
            onFilterChange={handleTimeWindowChange}
            options={timeWindowOptions}
            resetButtonText={t('filterTimeWindowClear')}
            titleText={t('filterTimeWindowListTitle')}
            value={filters.timeWindow}
          />
        </FilterController>
        {locationFilterElement}
        <FilterController name="categories" titleText={t('filterCategoryTitle')}>
          <FilterList
            isMulti
            isSearchable
            onFilterChange={handleCategoryChange}
            options={[...categoryOptions]}
            resetButtonText={t('filterCategoryClear')}
            titleText={t('filterCategoryListTitle')}
            value={filters.categories}
          />
        </FilterController>
        <FilterController name="metrics" titleText={t('filterMetricTitle')}>
          <FilterList
            isMulti
            isSearchable
            onFilterChange={handleMetricChange}
            options={[...metricOptions]}
            resetButtonText={t('filterMetricClear')}
            titleText={t('filterMetricListTitle')}
            value={filters.metrics}
          />
        </FilterController>
        <FilterController name="statuses" titleText={t('filterStatusTitle')}>
          <FilterList
            isMulti
            onFilterChange={handleStatusChange}
            options={[...statusOptions]}
            resetButtonText={t('filterStatusClear')}
            titleText={t('filterStatusListTitle')}
            value={filters.statuses}
          />
        </FilterController>
      </FilterBar>
    </div>
  );
}
